import { View } from "@/components/ui";
import { cn } from "@/lib/utils";
import React, { useId } from "react";
import type { ControllerProps, FieldPath, FieldValues } from "react-hook-form";
import { Controller } from "react-hook-form";
import { FormControlError } from "../form-control-error";
import { Typography } from "../typography";
import { SelectInput } from "../select-input";

interface SelectInputFormControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<ControllerProps<TFieldValues, TName>, "render"> {
  options: Array<{ label: string; value: string }>;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  rightRender?: React.ReactNode;
  hideErrorMessage?: boolean;
  fullWidth?: boolean;
}

export const SelectInputFormControl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  placeholder,
  label,
  className,
  disabled = false,
  rightRender,
  hideErrorMessage = false,
  fullWidth = false,
  ...props
}: SelectInputFormControlProps<TFieldValues, TName>) => {
  const id = useId();
  return (
    <View className={cn("pb-3", className)}>
      {label && (
        <Typography htmlFor={id} variant="label-sm" className="block pb-1">
          {label}
        </Typography>
      )}
      <Controller
        {...props}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <View className="flex flex-row gap-2">
              <SelectInput
                options={options}
                placeholder={placeholder}
                value={value}
                onChange={(val: string) => {
                  onChange(val);
                }}
                fullWidth={fullWidth}
                className={
                  error ? "border-destructive" : "border-secondary-border"
                }
                disabled={disabled}
              />
              {rightRender}
            </View>
            {!hideErrorMessage && <FormControlError error={error?.message} />}
          </>
        )}
      />
    </View>
  );
};
